import React from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import Stars from "./Stars";
import { optimizedImage, optimizedSrcSet } from "../../utils/cloudinaryImage";
import {
  catalogStorePath,
  handleCatalogAdd,
  isInhouse,
  vendorTypeLabel,
} from "../../utils/cartRouting";
import CrawlLink from "../CrawlLink";
import { formatCurrency } from "../../config/market";
import { isQuoteOnly, priceLabel } from "../../utils/priceOnRequest";

// ── Vendor-type accent (solid colours, no gradients) ──
const VT_COLOR = {
  inhouse: "#03660C",
  retailer: "#2f80ed",
  wholesaler: "#8e44ad",
  brand: "#e08a1e",
};

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #eef1f2;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
  box-shadow: 0 1px 4px rgba(20, 40, 45, 0.08);
  transition: box-shadow 0.16s ease, transform 0.16s ease;
  &:hover {
    box-shadow: 0 10px 26px rgba(20, 40, 45, 0.14);
    transform: translateY(-3px);
  }
  /* Touch screens have no hover, so give the resting card a deeper shadow
     for more separation on mobile. */
  @media (max-width: 760px) {
    box-shadow: 0 4px 16px rgba(20, 40, 45, 0.16);
  }
`;
const ImgWrap = styled.div`
  position: relative;
  width: 100%;
  padding-top: 100%;
  background: #ffffff;
  border-bottom: 1px solid #f0f3f4;
  cursor: pointer;
`;
const Img = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 12px;
  display: block;
`;
const VendorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #4a5b61;
  margin-bottom: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const Dot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-block;
  background: ${(p) => p.color};
`;
const SoldOut = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.66);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1px;
  color: #b03a3a;
`;
const Body = styled.div`
  padding: 10px 12px 12px;
  display: flex;
  flex-direction: column;
  flex: 1;
`;
const Cat = styled.div`
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6b7a80;
  font-weight: 600;
`;
const Name = styled.div`
  font-size: 13.5px;
  font-weight: 600;
  color: #732509;
  margin: 3px 0 5px;
  line-height: 1.3;
  min-height: 34px;
  cursor: pointer;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
  word-break: break-word;
`;
const RateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #6b7a80;
  margin-bottom: 7px;
  min-height: 16px;
`;
const Meta = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-top: auto;
  gap: 8px;
`;
const Price = styled.div`
  font-size: ${(p) => (p.$quote ? "12px" : "15px")};
  font-weight: 800;
  color: ${(p) => (p.$quote ? "#6b7a80" : "#0f9aa8")};
`;
const Pack = styled.div`
  font-size: 11px;
  color: #6b7a80;
  margin-top: 2px;
`;
const ActionBtn = styled.button`
  flex-shrink: 0;
  border: 1.5px solid #03660c;
  color: ${(p) => (p.$solid ? "#fff" : "#03660c")};
  background: ${(p) => (p.$solid ? "#03660c" : "#fff")};
  border-radius: 9px;
  height: 34px;
  min-width: 34px;
  padding: 0 10px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background 0.15s ease, color 0.15s ease;
  &:hover {
    background: #03660c;
    color: #fff;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
const InCart = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #037d30;
  background: #e9f6ee;
  border: 1px solid #bfe6cd;
  border-radius: 8px;
  padding: 6px 10px;
`;

const CartIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
  </svg>
);
const StoreIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 9l1-5h16l1 5" /><path d="M4 9v11h16V9" /><path d="M9 22V12h6v10" />
  </svg>
);
const QuoteIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const ProductCard = ({ product }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const cart = useSelector((state) => state.cart.products);

  const {
    _id, name, image, product_image, vendorType, vendorName,
    countInStock, ratingAverage = 0, ratingCount = 0, measurements, unit_id, categoryNames,
  } = product || {};

  const src = image || product_image;
  const inStock = Number(countInStock) > 0;
  const inhouse = isInhouse(product);
  const alreadyInCart =
    inhouse && cart?.some((p) => String(p._id) === String(_id));
  const accent = VT_COLOR[vendorType] || "#6b7a80";
  const catLabel = Array.isArray(categoryNames) && categoryNames.length ? categoryNames[0] : "";
  /* A quote item: the catalogue lists price-on-request stock now (it used to
     drop it, and a category of nothing else read as empty). No price to add
     at, so the action is the ask: the vendor's page, where the quote flow
     lives. priceLabel prints "Price on request" for it. */
  const quoteOnly = isQuoteOnly(product);

  const openDetail = () => {
    if (inhouse) history.push(`/product/${_id}`);
    else handleCatalogAdd(product, { dispatch, history });
  };
  // Where openDetail lands, as a real href for search engines and new tabs.
  const detailPath = inhouse ? `/product/${_id}` : catalogStorePath(product);
  const withLink = (children, style) =>
    detailPath ? (
      <CrawlLink to={detailPath} onNavigate={openDetail} style={style}>
        {children}
      </CrawlLink>
    ) : (
      children
    );

  const onAction = (e) => {
    e.stopPropagation();
    handleCatalogAdd(product, { dispatch, history });
  };

  return (
    <Card>
      <ImgWrap onClick={openDetail} title={name}>
        {src
          ? withLink(
              <Img
                src={optimizedImage(src, "card")}
                srcSet={optimizedSrcSet(src, "card")}
                sizes="(max-width: 600px) 45vw, 240px"
                loading="lazy"
                decoding="async"
                alt={name || "Product"}
              />,
              { display: "block" },
            )
          : null}
        {!inStock && <SoldOut>SOLD OUT</SoldOut>}
      </ImgWrap>

      <Body>
        <VendorRow title={vendorName}>
          <Dot color={accent} />
          {vendorName || vendorTypeLabel(vendorType)}
        </VendorRow>
        {catLabel ? <Cat>{catLabel}</Cat> : null}
        <Name onClick={openDetail}>{withLink(name)}</Name>

        <RateRow>
          {ratingCount > 0 ? (
            <>
              <Stars value={ratingAverage} />
              <span>
                {Number(ratingAverage).toFixed(1)} ({ratingCount})
              </span>
            </>
          ) : (
            <span style={{ color: "#9aa7ac" }}>No ratings yet</span>
          )}
        </RateRow>

        <Meta>
          <div>
            <Price $quote={quoteOnly}>{priceLabel(product, formatCurrency)}</Price>
            {measurements || unit_id?.unit ? (
              <Pack>
                {measurements} {unit_id?.unit || ""}
              </Pack>
            ) : null}
          </div>

          {quoteOnly ? (
            <ActionBtn onClick={onAction} title="Request a quote">
              <QuoteIcon /> Quote
            </ActionBtn>
          ) : alreadyInCart ? (
            <InCart>In cart</InCart>
          ) : inhouse ? (
            <ActionBtn $solid onClick={onAction} disabled={!inStock} title="Add to cart">
              <CartIcon />
            </ActionBtn>
          ) : (
            <ActionBtn onClick={onAction} title="Visit store">
              <StoreIcon /> Store
            </ActionBtn>
          )}
        </Meta>
      </Body>
    </Card>
  );
};

ProductCard.propTypes = { product: PropTypes.object };

export default ProductCard;
